import { ReactElement, useEffect, useState } from 'react';
import { getPublishedGamesList } from '../../api/publish';
import { useAppDispatcher, useAppSelector } from '../../hooks/redux';
import { IAPIResponse } from '../../interfaces/api';
import { IGameDraftUpdateResponse, IGameInfo, IndexedGameInfo } from '../../interfaces/game-info';
import { IUserInfoState } from '../../interfaces/slices';
import { addPublishedGames } from '../../slices/published-games';
import { KebabMenu } from '../KebabMenu';
import { LoadingIndicator } from '../Loading';
import { NoDataAvailable } from '../NoDataAvailable';
import defaultGameIcon1 from '../../assets/icons/default-game-icon-1.svg';
import optionsIcon from '../../assets/icons/options-icon.svg';
import navigationButton2 from '../../assets/icons/navigation-button-2.svg';
import './index.scss';
import { AnimatePresence } from 'framer-motion';
import { ConfirmationPopupWithReason, NewGamePopup, NotificationPopup } from '../Popup';
import { IPublishedGamesProps } from '../../interfaces/panel';
import { useRefreshEventSubscriber } from '../../hooks/rxjs';
import { updateDraft } from '../../api/draft';
import { checkAPIResponse } from '../../utils/utils';
import { log } from '../../utils/logger';

export const PublishedGamesPanel = (props: IPublishedGamesProps): ReactElement => {
    const dispatcher = useAppDispatcher();
    const userInfo: IUserInfoState = useAppSelector((state) => state.userInfo);
    const publishedGameInfo: IndexedGameInfo = useAppSelector((state) => state.publishedGames.info);
    const [isPublishedGamesListLoading, setPublishedGamesListLoadingStatus] = useState<boolean>(false);
    const [newGamePopupVisibility, setNewGamePopupVisibility] = useState<{ isVisible: boolean, gameInfo: IGameInfo | null }>({ isVisible: false, gameInfo: null });
    const [deleteGamePopup, setDeleteGamePopup] = useState<{ visibility: boolean, title: string, message: string, onConfirmation: Function, onCancellation: Function }>({ visibility: false, title: '', message: '', onConfirmation: () => { }, onCancellation: () => { } });
    const [notificationPopup, setNotificationPopup] = useState<{ visibility: boolean, message: string }>({ visibility: false, message: '' });

    const getPublishedGames = async (): Promise<void> => {
        setPublishedGamesListLoadingStatus(true);
        const publishedGamesList: IAPIResponse<Array<IGameInfo>> = await getPublishedGamesList<IGameInfo>(userInfo.lanid);
        const formattedResponse = checkAPIResponse<Array<IGameInfo>>(publishedGamesList);

        if (formattedResponse.status === true && publishedGamesList.response != null && publishedGamesList.response.data != null) {
            dispatcher(addPublishedGames(publishedGamesList.response.data));
        }
        else {
            if (formattedResponse.message != null) {
                log.Error(`Unable to load published games (${formattedResponse.message})`);
            }
            else {
                log.Error(`Unable to load published games`);
            }
        }

        setPublishedGamesListLoadingStatus(false);
    }

    const onKebabMenuOptionSelected = (option: 'edit' | 'delete', gameName: string): void => {
        const selectedGameInfo: IGameInfo = publishedGameInfo[gameName];

        if (selectedGameInfo != null) {
            switch (option) {
                case 'edit':
                    setNewGamePopupVisibility({ isVisible: true, gameInfo: selectedGameInfo });
                    break;
                case 'delete':
                    setDeleteGamePopup({
                        visibility: true,
                        title: 'Delete Game',
                        message: `Are you sure you want to delete game ${gameName}?`,
                        onConfirmation: (reason: string) => saveDeletedGameAsDraft(selectedGameInfo, reason),
                        onCancellation: hideDeleteGamePopup
                    });
                    break;
            }
        }
    }

    const hideDeleteGamePopup = (): void => {
        setDeleteGamePopup({ visibility: false, title: '', message: '', onConfirmation: () => { }, onCancellation: () => { } })
    }

    const saveDeletedGameAsDraft = async (gameInfo: IGameInfo, reason: string) => {
        hideDeleteGamePopup();
        let selectedGameInfo: IGameInfo = { ...gameInfo };

        selectedGameInfo.operation = 'delete';
        selectedGameInfo.modifyReason = reason;

        const apiResponse: IAPIResponse<IGameDraftUpdateResponse> = await updateDraft(userInfo.lanid, selectedGameInfo);
        const formattedResponse = checkAPIResponse<IGameDraftUpdateResponse>(apiResponse);

        if (formattedResponse.status === false) {
            setNotificationPopup({ visibility: true, message: `Unable to save game ${selectedGameInfo.gameName} as a draft (${formattedResponse.message})` });
        }
    }

    const onNewGameClosePopup = (): void => {
        setNewGamePopupVisibility({ isVisible: false, gameInfo: null })
    }

    const onNotificationPopupClose = (): void => {
        setNotificationPopup({ visibility: false, message: '' });
    }

    useEffect(() => {
        getPublishedGames();
    }, []);

    useRefreshEventSubscriber('published', getPublishedGames);

    return <div className='game-list-wrapper'>
        <div className='game-list-header-container'>
            <img src={navigationButton2} alt='' />
            <span>List of Games</span>
        </div>
        <div className='game-list-body-container'>
            {isPublishedGamesListLoading === true ? <LoadingIndicator /> :
                <>
                    {Object.values(publishedGameInfo).length > 0 ?
                        <>
                            {Object.values(publishedGameInfo).map((item: IGameInfo, index: number) => (
                                <div className='game-info-container' key={`published-game-container-${index}`}>
                                    <div className='game-info-image-container'>
                                        <img src={defaultGameIcon1} alt='' onClick={() => props.setSelectedGameName(item.gameName)} title="Click to Preview game" />
                                    </div>
                                    <div className='game-info-details-container'>
                                        <span className='game-name-text' onClick={() => props.setSelectedGameName(item.gameName)} title="Click to Preview game">{item.gameName}</span>
                                        <span className='game-status-text' onClick={() => props.setSelectedGameName(item.gameName)} title="Click to Preview game">{`Type: ${item.status}`}</span>
                                        <span className='game-description-text' onClick={() => props.setSelectedGameName(item.gameName)} title="Click to Preview game">{item.description}</span>
                                    </div>
                                    <div className='game-info-miscellaneous-container'>
                                        {userInfo.roles.maker === true &&
                                            <KebabMenu
                                                optionsIcon={optionsIcon}
                                                gameName={item.gameName}
                                                onOptionClicked={(option: 'edit' | 'delete', gameName: string) => onKebabMenuOptionSelected(option, gameName)}
                                            />
                                        }
                                    </div>
                                </div>
                            ))
                            }
                        </>
                        :
                        <div className='published-no-data-container'>
                            <div className='published-no-data-wrapper'>
                                <NoDataAvailable />
                            </div>
                        </div>
                    }
                </>
            }
        </div>
        <AnimatePresence>
            {/* Another AnimatePresence is added to fix this issue https://tetherfi-pte-ltd.atlassian.net/browse/PRODQA-600 most probably caused in framer-motion(https://github.com/framer/motion/pull/1569) */}
            {notificationPopup.visibility === true &&
                <NotificationPopup
                    message={notificationPopup.message}
                    closePopup={onNotificationPopupClose}
                />
            }
        </AnimatePresence>
        <AnimatePresence>
            {newGamePopupVisibility.isVisible === true &&
                <NewGamePopup
                    isApprovalMode={false}
                    updationGameInfo={newGamePopupVisibility.gameInfo}
                    closePopup={onNewGameClosePopup}
                />
            }
            {deleteGamePopup.visibility === true &&
                <ConfirmationPopupWithReason
                    title={deleteGamePopup.title}
                    message={deleteGamePopup.message}
                    isModifyReasonRequired={true}
                    onConfirmation={deleteGamePopup.onConfirmation}
                    onCancellation={deleteGamePopup.onCancellation}
                />
            }
        </AnimatePresence>
    </div>
}